import { Trans } from '@lingui/macro';
import { createContext, useContext, useState } from "react";
import { Message } from "../../../models/Message";
import { Room } from "../../../models/Room";
import { AddFromMyRoomFilesModal } from "../AddFromMyRoomFilesModal";
import { ScreenRecordModalEmitter } from "../ScreenRecordModal";
import { VoiceRecordModalEmitter } from "./MsgRecordModal";
import { RoomItem } from "../../../models/RoomItem";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Button } from "../../../components/ui/button";
import { Video, Paperclip, FileText, Mic, X } from 'lucide-react';
import { IconPlus } from '../FigmaIcons';

const MsgAddMorePopover: React.FC<{ room: Room, parentMessage: Message | undefined, children?: React.ReactNode }> = ({ room, parentMessage, children }) => {
  const { files, setFiles, setItems } = useContext(UploadFilesContext);
  const [showMyRoomModal, setShowMyRoomModal] = useState(false);
  const [open, setOpen] = useState(false);

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {children ? children : (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-[color:rgb(var(--text-body))] transition-colors"
              data-cy="plus-btn"
            >
              <IconPlus className="h-5 w-5" />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={5} className="w-56 z-[2000]">
          <DropdownMenuItem onClick={recordVideo} className="cursor-pointer py-3">
            <Video className="mr-3 h-5 w-5 text-[color:rgb(var(--text-body))]" />
            <Trans>Screen Recording</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={recordAudio} className="cursor-pointer py-3">
            <Mic className="mr-3 h-5 w-5 text-[color:rgb(var(--text-body))]" />
            <Trans>Record & Recognize</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={selectFilesToUpload} className="cursor-pointer py-3">
            <Paperclip className="mr-3 h-5 w-5 text-[color:rgb(var(--text-body))]" />
            <Trans>Upload Files</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={addFromMyRoom} className="cursor-pointer py-3">
            <FileText className="mr-3 h-5 w-5 text-[color:rgb(var(--text-body))]" />
            <Trans>Add from My Channel</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setOpen(false)} className="cursor-pointer py-3" data-cy="cancel">
            <X className="mr-3 h-5 w-5 text-[color:rgb(var(--text-body))]" />
            <Trans>Cancel</Trans>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <input id="file-input" type='file' onChange={filesSelected} hidden multiple />
      {showMyRoomModal && <AddFromMyRoomFilesModal onCancel={() => setShowMyRoomModal(false)} onDone={doneAddFromMyRoomFiles} />}
    </>
  )

  function recordVideo() {
    ScreenRecordModalEmitter.startRecording(room, parentMessage);
    setOpen(false);
  }

  function recordAudio() {
    VoiceRecordModalEmitter.startRecording();
    setOpen(false);
  }

  function selectFilesToUpload() {
    setOpen(false);
    document.getElementById('file-input')?.click();
  }

  async function filesSelected(e: any) {
    const cnt = e.target.files.length;
    let newFiles: File[] = [];
    for (let i = 0; i < cnt; i++) {
      const file = e.target.files[i];
      if (files.find(f => f.name === file.name)) {
        continue;
      }
      newFiles.push(file);
    }
    if (e.target.files) {
      setFiles([...newFiles, ...files]);
      e.target.value = '';
    }
  }

  function addFromMyRoom() {
    setOpen(false);
    setShowMyRoomModal(true);
  }

  function doneAddFromMyRoomFiles(selectedItems: RoomItem[]) {
    setShowMyRoomModal(false);
    setItems(selectedItems);
  }
}

type IUploadFilesContext = {
  files: File[], setFiles: (files: File[]) => void,
  items: RoomItem[], setItems: (items: RoomItem[]) => void
}
export const UploadFilesContext = createContext<IUploadFilesContext>({
  files: [],
  setFiles: (p: File[]) => { },
  items: [],
  setItems: (p: RoomItem[]) => { }
});

export default MsgAddMorePopover;
